import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categoryVideos, categoryNames } from '@/mocks/categoryData';
import { categories } from '@/mocks/homeData';

const categoryImageUrls: Record<string, string[]> = {
  all: [
    'https://readdy.ai/api/search-image?query=Chinese%20historical%20drama%20poster%20with%20ancient%20warrior%20in%20golden%20palace%2C%20warm%20amber%20lighting%2C%20cinematic%20atmosphere%2C%20clean%20background%20with%20traditional%20architecture&width=600&height=800&seq=cat-all-01&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Sci-fi%20movie%20poster%20with%20astronaut%20in%20deep%20space%2C%20warm%20golden%20nebula%2C%20cinematic%20lighting%2C%20clean%20dramatic%20composition&width=600&height=800&seq=cat-all-02&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Variety%20show%20poster%20with%20colorful%20stage%20lights%20in%20warm%20amber%20tones%2C%20energetic%20entertainment%20mood%2C%20clean%20poster%20design&width=600&height=800&seq=cat-all-03&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Food%20documentary%20poster%20with%20steaming%20Chinese%20cuisine%20on%20wooden%20table%2C%20warm%20golden%20light%2C%20intimate%20close-up&width=600&height=800&seq=cat-all-04&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Anime%20fantasy%20poster%20with%20young%20warrior%20and%20golden%20flame%20energy%2C%20dramatic%20cinematic%20lighting%2C%20clean%20illustration&width=600&height=800&seq=cat-all-05&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Elegant%20variety%20show%20poster%20with%20graceful%20silhouettes%20in%20soft%20warm%20spotlight%2C%20glamorous%20atmosphere&width=600&height=800&seq=cat-all-06&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Modern%20Chinese%20drama%20poster%20with%20professional%20in%20city%20at%20sunset%2C%20warm%20golden%20hour%20light%2C%20sophisticated%20mood&width=600&height=800&seq=cat-all-07&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Chinese%20animation%20movie%20poster%20with%20mythical%20hero%20surrounded%20by%20flames%2C%20warm%20golden%20energy%2C%20epic%20lighting&width=600&height=800&seq=cat-all-08&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Historical%20Chinese%20drama%20poster%20with%20scholar%20in%20ancient%20library%2C%20warm%20candlelight%2C%20mysterious%20atmosphere&width=600&height=800&seq=cat-all-09&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Chinese%20historical%20movie%20poster%20with%20soldiers%20in%20ancient%20alleyway%2C%20warm%20lantern%20light%2C%20suspenseful%20mood&width=600&height=800&seq=cat-all-10&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Talent%20show%20poster%20with%20young%20performers%20on%20colorful%20stage%2C%20warm%20spotlights%2C%20youthful%20energy&width=600&height=800&seq=cat-all-11&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Aerial%20landscape%20poster%20of%20beautiful%20Chinese%20mountains%20and%20rivers%2C%20warm%20sunlight%2C%20breathtaking%20vista&width=600&height=800&seq=cat-all-12&orientation=portrait',
  ],
};

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.douhuavideo.com/' },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://www.douhuavideo.com/category/all' },
      ],
    },
    {
      '@type': 'CollectionPage',
      name: '豆花视频 - 分类浏览',
      description: '按类别浏览豆花视频平台的海量影视内容，包含电视剧、电影、综艺、动漫、纪录片等多种类型。',
      url: 'https://www.douhuavideo.com/category/all',
    },
  ],
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const activeCategory = slug || 'all';
  const [visibleCount, setVisibleCount] = useState(8);
  const [sortBy, setSortBy] = useState('default');

  const videos = categoryVideos[activeCategory] || categoryVideos.all;

  useEffect(() => {
    setVisibleCount(8);
    setSortBy('default');
  }, [activeCategory]);

  const sortedVideos = [...videos].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'views') return parseFloat(b.views.replace('亿', '')) - parseFloat(a.views.replace('亿', ''));
    return 0;
  });

  const displayedVideos = sortedVideos.slice(0, visibleCount);
  const hasMore = visibleCount < videos.length;

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <nav className="w-full px-4 md:px-6 py-4 bg-background-50 border-b border-background-200/70" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-900 font-medium">
              {categoryNames[activeCategory] || '全部内容'}
            </li>
          </ol>
        </nav>

        {/* Category Tabs */}
        <section className="w-full py-6 bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              分类浏览
            </h1>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => navigate(`/category/${cat.id}`)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700 border border-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Sort Bar */}
        <section className="w-full py-4 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-900">{videos.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序：</span>
                {[
                  { value: 'default', label: '默认' },
                  { value: 'rating', label: '评分最高' },
                  { value: 'views', label: '播放最多' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="w-full py-6 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {displayedVideos.map((video) => {
                const imgIdx = (video.id % 12);
                const imgUrl = categoryImageUrls.all?.[imgIdx] || '';
                return (
                  <div key={video.id} className="group cursor-pointer">
                    <div className="relative rounded-lg overflow-hidden bg-background-50">
                      <div className="relative w-full aspect-[3/4] overflow-hidden">
                        {imgUrl && (
                          <img
                            src={imgUrl}
                            alt={video.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            title={`豆花视频 - ${video.title}`}
                          />
                        )}
                        <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium rounded bg-accent-500 text-white">
                          {video.badge}
                        </span>
                        <div className="absolute top-2 right-2 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-black/50 text-white text-xs">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                          {video.rating}
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <i className="ri-play-fill text-xl text-primary-600"></i>
                          </div>
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 mb-1">{video.title}</h3>
                        <p className="text-xs text-foreground-500 line-clamp-1 mb-1.5">{video.description}</p>
                        <div className="flex items-center gap-2 text-xs text-foreground-500">
                          <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 font-medium">{video.category}</span>
                          <span>{video.updateInfo}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setVisibleCount((prev) => prev + 8)}
                  className="px-8 py-3 rounded-full bg-background-50 border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  加载更多内容
                  <i className="ri-arrow-down-line ml-1.5"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}